import $ from 'jquery';
import PropTypes from 'prop-types';
import React, {HTMLAttributes} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert, assertAndContinue} from 'Asserts';
import ExternalFileUpload from 'external-files/ExternalFileUpload';
import NoteConstants from 'notebook/notes/NoteConstants';
// @ts-expect-error
import TFIFileImport from 'notebook/tfi/TFIFileImport';
import {
  AttachedExternalFileAddedVia,
  trackAttachedExternalFile,
} from 'notebook/util/analytics/FileAnalyticsUtils';
import {FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {AttachDropdownItem, LinkedItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import BenchlingData, {
  FILES_MIME_TYPE,
  SOURCES_MIME_TYPE,
  TFI_MIME_TYPE,
  TFIJson,
} from 'util/drag-and-drop/BenchlingData';
import DropTarget from 'util/drag-and-drop/DropTarget';
import * as modals from 'util/Modals';
import {capitalize, typeToName, typeToPluralized} from 'util/NameUtil';
import * as net from 'util/Net';
import {ElementDragEvent, ElementMouseEvent} from 'util/Types';

const SUPPORTED_MIME_TYPES = ['Files', SOURCES_MIME_TYPE, FILES_MIME_TYPE, TFI_MIME_TYPE];

export interface EnrichedFileUploadTask {
  taskId: string;
  fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>;
  fileType?: string;
  fileName?: string;
}

interface NoteDropTargetProps {
  disableDropMessage?: string;
  locked: boolean;
  // TODO(BNCH-112435): Make this required
  isProtected?: boolean;
  className?: string;
  fileId: string;
  onSetDropEffect?: (effect: string) => void;
  onMouseDown?: (e: ElementMouseEvent) => void;
  handleDropExternalFiles: (taskIdAndDeferreds: Array<EnrichedFileUploadTask>) => void;
  handleDropBenchlingFile: (item: LinkedItem) => void;
  filterDropTargetFiles?: (files: FileList) => Array<File>;
  multiple: boolean;
  hasExistingItem: boolean;
  tabIndex?: HTMLAttributes<HTMLDivElement>['tabIndex'];
}

class NoteDropTarget extends React.Component<NoteDropTargetProps> {
  static propTypes = {
    disableDropMessage: PropTypes.string,
    locked: PropTypes.bool.isRequired,
    className: PropTypes.string,
    fileId: PropTypes.string.isRequired,
    onSetDropEffect: PropTypes.func,
    onMouseDown: PropTypes.func,
    handleDropExternalFiles: PropTypes.func.isRequired,
    handleDropBenchlingFile: PropTypes.func.isRequired,
    // Determines whether we only call handleDropExternalFiles with the first dropped file, or all of
    // the dropped files
    multiple: PropTypes.bool.isRequired,
    hasExistingItem: PropTypes.bool.isRequired,
  };

  _getDroppedItemFromEvent(e: ElementDragEvent): JQueryPromise<LinkedItem> | null {
    // eslint-disable-next-line new-cap
    const bd = BenchlingData(e);
    const files = bd.getData<typeof FILES_MIME_TYPE>(FILES_MIME_TYPE) || [];
    if (!files.length) {
      // data validation may have failed in BenchlingData
      notifications.warn(DropTarget.dropErrorMessage('note'));
      return null;
    }
    if (!assertAndContinue(files.length === 1, `Expected one file to link. Found ${files.length}`)) {
      notifications.warn('Linking multiple files is not currently allowed.');
      return null;
    }

    const [file]: Array<{id: string}> = Array.from(files);
    const fileType = APIUtil.apiIdToType(file.id);
    if (file.id === this.props.fileId) {
      notifications.warn(`${capitalize(typeToPluralized(fileType))} cannot be linked to \
themselves.`);
      return null;
    }
    if (!Array.from(NoteConstants.NOTE_PREVIEW_SUPPORTED_TYPES).includes(fileType)) {
      notifications.warn(`${capitalize(typeToName(fileType))} cannot be linked to notes.`);
      return null;
    }

    // Fetch link dict for file.
    LoadingScreen.show('Linking...');
    // TODO(T8957): Generalize to work for non-folder-items
    const url = `/1/api/files/link-search?fromFileId=${this.props.fileId}`;
    const promiseForLinkedItem = net
      .getJSON(url, {id: file.id})
      .always(() => {
        LoadingScreen.hide();
      })
      .then((results: Array<AttachDropdownItem>) => {
        if (
          !assertAndContinue(
            results.length === 1,
            `Expected one result from ${url}?id=${file.id}. \
Found ${results.length}`
          )
        ) {
          return $.Deferred().reject().promise();
        }
        const [result] = Array.from(results);
        // eslint-disable-next-line no-restricted-syntax
        const item = _.extend({id: result.item_id}, result.item);
        return item;
      });
    assert(Boolean(promiseForLinkedItem), 'Expecting that user dropped a Benchling item');
    return promiseForLinkedItem;
  }

  _handleDropExternalFiles(files: FileList): void {
    const allFiles = this.props.filterDropTargetFiles
      ? this.props.filterDropTargetFiles(files)
      : Array.from(files);
    const filesToUpload = this.props.multiple ? allFiles : allFiles.slice(0, 1);
    const taskIdAndDeferreds = ExternalFileUpload.createTaskAndUploadFiles(this.props.fileId, filesToUpload);
    const enrichedTasks = taskIdAndDeferreds.map((task, i) => ({
      ...task,
      fileType: filesToUpload[i]?.type,
      fileName: filesToUpload[i]?.name,
    }));
    this.props.handleDropExternalFiles(enrichedTasks);

    for (const file of filesToUpload) {
      trackAttachedExternalFile(
        this.props.fileId,
        AttachedExternalFileAddedVia.DragAndDrop,
        file.type,
        file.size,
        file.name
      );
    }
  }

  _handleDropTFIFile(tfiObject: TFIJson): void {
    const taskIdAndDeferred = TFIFileImport.createTaskAndImportFile(this.props.fileId, tfiObject);
    this.props.handleDropExternalFiles([taskIdAndDeferred]);
    trackAttachedExternalFile(this.props.fileId, AttachedExternalFileAddedVia.TFI);
  }

  _handleDropBenchlingFile(linkedBenchlingItemPromise: JQueryPromise<LinkedItem>): void {
    linkedBenchlingItemPromise
      .done((item: LinkedItem) => {
        this.props.handleDropBenchlingFile(item);
        analytics.track('lab_notebook', 'linked_benchling_file', {
          file_id: this.props.fileId,
          linked_file_id: item.id,
        });
      })
      .fail(notifications.notifyError);
  }

  confirmIfReplaceAndInvoke(func: () => void): void {
    if (this.props.hasExistingItem) {
      modals
        .confirm(
          'Replace existing item',
          'Are you sure you want to replace the existing item with a new one?' +
            " You'll still be able to access the old item by opening a previous version of the entry."
        )
        .done(func);
    } else {
      func();
    }
  }

  onDrop = (e: ElementDragEvent): void => {
    // If there are files in the dataTransfer, then the user dropped external (OS) files
    const files = e.dataTransfer.files;
    if (files.length) {
      this.confirmIfReplaceAndInvoke(() => {
        this._handleDropExternalFiles(files);
      });
      return;
    }

    // If a TFI file is being dragged, import and attach it
    // eslint-disable-next-line new-cap
    const bd = BenchlingData(e);
    const tfiObject = bd.getData<typeof TFI_MIME_TYPE>(TFI_MIME_TYPE);
    if (tfiObject) {
      this.confirmIfReplaceAndInvoke(() => {
        this._handleDropTFIFile(tfiObject);
      });
      return;
    }

    // Otherwise, check if a user dropped a Benchling Item
    const linkedBenchlingItemPromise = this._getDroppedItemFromEvent(e);
    if (linkedBenchlingItemPromise) {
      this.confirmIfReplaceAndInvoke(() => {
        this._handleDropBenchlingFile(linkedBenchlingItemPromise);
      });
    }
  };

  validateDrop = (e: ElementDragEvent): string | null => {
    if (this.props.disableDropMessage != null) {
      return this.props.disableDropMessage;
    }
    const itemBlockReason = $(e.target)
      .closest('[data-mediocre-item-block-dnd]')
      .attr('data-mediocre-item-block-dnd');
    if (itemBlockReason) {
      return itemBlockReason;
    }
    // eslint-disable-next-line new-cap
    const bd = BenchlingData(e);
    if (this.props.locked) {
      const fileTypeName = typeToName(APIUtil.apiIdToType(this.props.fileId));
      return `Unable to attach dragged item: ${fileTypeName} is not writable.`;
    } else if (this.props.isProtected) {
      return `Unable to attach dragged item to Fill-in Entry.`;
    } else if (bd.hasData(SOURCES_MIME_TYPE)) {
      return 'A project cannot be attached to a notebook entry.';
    } else {
      return null;
    }
  };

  override render(): JSX.Element {
    return (
      <DropTarget
        className={this.props.className}
        shouldCaptureDrop={true}
        mimeTypes={SUPPORTED_MIME_TYPES}
        validateDrop={this.validateDrop}
        dropEffect="link"
        targetDesc="note"
        role="group"
        aria-label="file drop target"
        onDrop={this.onDrop}
        onSetDropEffect={this.props.onSetDropEffect}
        // @ts-expect-error ts-migrate FIXME no props.onMouseDown
        onMouseDown={this.props.onMouseDown}
        tabIndex={this.props.tabIndex}
      >
        {this.props.children}
      </DropTarget>
    );
  }
}

export default NoteDropTarget;
